import { ReactNode, useEffect, useRef } from 'react'
import { SvgCross, SvgDrag } from '../assets/images'
import { FocusTrap } from 'focus-trap-react'
import Draggable from 'react-draggable'
import { createPortal } from 'react-dom'
import { v4 as uuid } from 'uuid'

interface ModalProps {
  children: ReactNode
  isOpen: boolean
  closeModal: () => void
  modalHeader?: string
  headerClasses?: string
  className?: string
  showCloseButton?: boolean
  width?: string
  closeModalOnOutsideClick?: boolean
  cssClassName?: string
  bodyCssClassName?: string
  allowOutsideClick?: boolean
  isDragable?: boolean
  preventBackgroundInteraction?: boolean
}

const Modal = ({
  children,
  isOpen,
  closeModal,
  modalHeader,
  headerClasses,
  showCloseButton = true,
  width = 'w-fit max-w-[98%]',
  closeModalOnOutsideClick = false,
  cssClassName,
  bodyCssClassName,
  allowOutsideClick = true,
  isDragable = false,
  preventBackgroundInteraction = false,
}: ModalProps) => {
  const modalContainer = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const modal = modalContainer?.current
      const dropdownRoot = document.getElementById('dropdown-root')
      if (
        closeModalOnOutsideClick &&
        modal &&
        !modal.contains(event.target as Node) &&
        !dropdownRoot?.contains(event.target as Node)
      ) {
        closeModal()
      }
    }
    if (closeModalOnOutsideClick) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [closeModal, modalContainer])

  const previouslyFocusedElementRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    const modal = modalContainer.current
    if (!isOpen || !modal) return

    previouslyFocusedElementRef.current = document.activeElement as HTMLElement

    const focusableElements = Array.from(
      modal.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => el.offsetParent !== null)

    const firstEl = focusableElements[0]
    if (firstEl) firstEl.focus()
    else modal.focus()
    return () => {
      previouslyFocusedElementRef.current?.focus?.()
    }
  }, [isOpen])

  if (!isOpen) return null

  const handleClass = `drag-handle-${uuid()}`

  const modalContent = (
    <FocusTrap
      active={true}
      focusTrapOptions={{
        initialFocus: false,
        allowOutsideClick,
        fallbackFocus: () => modalContainer.current || document.body,
      }}
    >
      <div
        className={`fixed inset-0 flex justify-center items-center z-[40] ${
          isDragable
            ? 'bg-transparent pointer-events-none'
            : 'bg-black bg-opacity-50'
        } ${cssClassName ?? ''}`}
        onClick={(e) => preventBackgroundInteraction && e.stopPropagation()}
      >
        <button
          style={{
            opacity: 0,
            position: 'absolute',
            pointerEvents: 'none',
          }}
          tabIndex={0}
          aria-hidden="true"
        >
          Fallback
        </button>

        <Draggable
          handle={`.${handleClass}`}
          cancel=".modal-body-container input, textarea, select"
          bounds="body"
        >
          <div
            ref={modalContainer}
            className={`bg-accent-0 rounded-[1rem] shadow-lg ${
              isDragable
                ? 'pointer-events-auto px-5 pb-5 pt-6 border border-accent-350'
                : 'p-3'
            } ${width} ${bodyCssClassName ?? ''} relative${
              modalHeader ? '' : ' flex'
            }`}
            style={{ maxHeight: 'calc(100vh - 2rem)' }}
          >
            {isDragable && (
              <div className="absolute top-3 left-1/2 -translate-x-1/2 translate-y-[-50%] z-10">
                <button className={`${handleClass} cursor-move`}>
                  <SvgDrag className="rotate-90" />
                </button>
              </div>
            )}

            {modalHeader ? (
              <div className="border-b border-accent-100 pb-2">
                <div
                  className={`flex justify-between items-center bg-accent-50 rounded shadow pl-5 pr-1 py-4 ${
                    headerClasses ?? ''
                  }`}
                >
                  <div className="font-primary font-medium text-base">
                    {modalHeader}
                  </div>
                  {showCloseButton && (
                    <div
                      className="cursor-pointer p-1"
                      onClick={closeModal}
                      tabIndex={0}
                      role="button"
                      onKeyDown={(e) => {
                        if (e?.key === 'Enter' || e?.key === ' ') {
                          closeModal()
                          e?.preventDefault()
                        }
                      }}
                    >
                      <span className="text-black">
                        <SvgCross />
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              showCloseButton && (
                <div
                  className="absolute top-2 right-2 cursor-pointer p-1"
                  onClick={closeModal}
                  tabIndex={0}
                  role="button"
                  onKeyDown={(e) => {
                    if (e?.key === 'Enter' || e?.key === ' ') {
                      closeModal()
                      e?.preventDefault()
                    }
                  }}
                >
                  <span className="text-black">
                    <SvgCross />
                  </span>
                </div>
              )
            )}
            <div
              className="modal-scroll-container flex-1 overflow-y-auto hide-scrollbar"
              style={{ overscrollBehavior: 'contain' }}
            >
              <div className="modal-body-container w-full h-full">
                {children}
              </div>
            </div>
          </div>
        </Draggable>
      </div>
    </FocusTrap>
  )

  return createPortal(modalContent, document.body)
}

export default Modal
